import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { ReferralRequestService } from '../../services/referral-request.service';
import { BrandService } from '../../core/services/brand.service';
import { TokenStorageService } from '../../core/services/token-storage.service';
import { IReferralRequestInfo } from '../../models/referral-request.model';
import { singleEmailValidator } from '../../shared/validators/single-email-validator';
import { phoneValidator } from '../../shared/validators/phone-validator';
import { BookmarkBannerComponent } from '../../shared/bookmark-banner.component';

type Tab = 'email' | 'text';

@Component({
  selector: 'app-referral-request',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgxMaskDirective, BookmarkBannerComponent],
  providers: [provideNgxMask()],
  templateUrl: './referral-request.component.html',
  styleUrl: './referral-request.component.scss',
})
export class ReferralRequestComponent implements OnInit {
  protected brandService = inject(BrandService);
  private service = inject(ReferralRequestService);
  private tokenStorage = inject(TokenStorageService);
  private router = inject(Router);
  private fb = inject(FormBuilder);

  info: IReferralRequestInfo | null = null;
  isLoading = true;
  loadError = '';

  activeTab: Tab = 'email';

  emailSubmitting = false;
  emailError = '';
  emailSuccess = '';

  textSubmitting = false;
  textError = '';
  textSuccess = '';

  emailForm: FormGroup = this.fb.group({
    recipientEmail: [null, [Validators.required, singleEmailValidator]],
  });

  textForm: FormGroup = this.fb.group({
    recipientPhone: [null, [Validators.required, phoneValidator]],
  });

  get emailSubject(): string {
    return this.info ? `Referrals for ${this.info.businessName}` : '';
  }

  get emailHtmlPreview(): string {
    if (!this.info) {
      return '';
    }
    return this.buildEmailHtmlBody(
      this.info.businessName,
      this.info.domain,
      this.info.shopPhone ?? ''
    );
  }

  get smsPreview(): string {
    if (!this.info) {
      return '';
    }
    return this.buildSmsBody(
      this.info.businessName,
      this.info.domain,
      this.info.shopPhone ?? ''
    );
  }

  get mailtoHref(): string {
    if (!this.info) {
      return '#';
    }
    const body = this.buildPlainTextBody(
      this.info.businessName,
      this.info.domain,
      this.info.shopPhone ?? ''
    );
    return `mailto:?subject=${encodeURIComponent(this.emailSubject)}&body=${encodeURIComponent(body)}`;
  }

  ngOnInit(): void {
    this.service.getInfo().subscribe({
      next: data => {
        this.info = data;
        this.isLoading = false;
      },
      error: err => {
        this.isLoading = false;
        if (err.status === 401) {
          this.tokenStorage.clearToken();
          this.router.navigate(['/login']);
        } else if (err.status === 403) {
          this.loadError = 'This feature is not enabled for your account.';
        } else {
          this.loadError = 'Unable to load page. Please try again later.';
        }
      },
    });
  }

  setTab(tab: Tab): void {
    this.activeTab = tab;
  }

  submitEmail(): void {
    this.emailError = '';
    this.emailSuccess = '';

    if (this.emailForm.invalid) {
      this.emailError = 'There are errors in this form.';
      return;
    }

    this.emailSubmitting = true;

    this.service
      .sendEmail({
        recipientEmail: this.emailForm.value.recipientEmail!,
      })
      .subscribe({
        next: () => {
          this.emailSubmitting = false;
          this.emailSuccess = 'Email sent successfully.';
          this.emailForm.reset();
        },
        error: err => {
          this.emailSubmitting = false;
          if (err.status === 401 || err.status === 403) {
            this.tokenStorage.clearToken();
            this.router.navigate(['/login']);
          } else if (err.status === 400) {
            this.emailError = 'Please enter a valid email address.';
          } else {
            this.emailError = 'There was an error sending the email. Please try again.';
          }
        },
      });
  }

  submitText(): void {
    this.textError = '';
    this.textSuccess = '';

    if (this.textForm.invalid) {
      this.textError = 'There are errors in this form.';
      return;
    }

    this.textSubmitting = true;

    this.service
      .sendText({
        recipientPhone: this.textForm.value.recipientPhone!,
      })
      .subscribe({
        next: () => {
          this.textSubmitting = false;
          this.textSuccess = 'Text sent successfully.';
          this.textForm.reset();
        },
        error: err => {
          this.textSubmitting = false;
          if (err.status === 401 || err.status === 403) {
            this.tokenStorage.clearToken();
            this.router.navigate(['/login']);
          } else if (err.status === 400) {
            this.textError = 'Please enter a valid 10-digit phone number.';
          } else {
            this.textError = 'There was an error sending the text. Please try again.';
          }
        },
      });
  }

  private buildPlainTextBody(shop: string, domain: string, shopPhone: string): string {
    const phonePart = shopPhone ? `(${shopPhone}) ` : '';

    return [
      'We really appreciate your business!',
      '',
      'One of the highest compliments you can give us is to recommend us to family, friends, neighbors and colleagues. ' +
        this.brandService.brand.referralProspectSentence,
      '',
      `If so, please feel free to give them our number ${phonePart}and encourage them to give us a call. You can also make the referral directly on our website at http://www.${domain}/referral.php.`,
      '',
      'We thank you again for being a client!',
      `The Team at ${shop}`,
    ].join('\n');
  }

  private buildEmailHtmlBody(shop: string, domain: string, shopPhone: string): string {
    const phonePart = shopPhone ? `(${shopPhone}) ` : '';
    const referralUrl = `http://www.${domain}/referral.php`;

    return (
      'We really appreciate your business!<br><br>' +
      'One of the highest compliments you can give us is to recommend us to family, friends, neighbors and colleagues. ' +
      `${this.brandService.brand.referralProspectSentence}<br><br>` +
      `If so, please feel free to give them our number ${phonePart}and encourage them to give us a call. ` +
      `You can also make the referral directly on our website at <a href="${referralUrl}">${referralUrl}</a>.<br><br>` +
      `We thank you again for being a client!<br>The Team at ${shop}`
    );
  }

  private buildSmsBody(shop: string, domain: string, shopPhone: string): string {
    const phonePart = shopPhone ? ` ${shopPhone} ` : ' ';

    return (
      `${shop} really appreciates your business!\n\n` +
      'One of the highest compliments you can give us is to recommend us to family, friends, neighbors and colleagues. ' +
      `${this.brandService.brand.referralProspectSentence}\n\n` +
      `If so, please feel free to give them our number${phonePart}and encourage them to give us a call. ` +
      `You can also make the referral directly on our website at http://www.${domain}/referral.php.\n\nThank you!`
    );
  }
}
